import clsx from 'clsx';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ListViewStyleIcon } from '../../components/icons-library/list-view-style';
import { GalleryViewStyleIcon } from '../../components/icons-library/gallery-view-style';
import { useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';

type ButtonProps = {
    type: 'list' | 'gallery';
    text: string;
    onClick: (args?: any) => void;
    isActive: boolean;
};

/* Segmented view switch from the events design: the active option sits on a neutral-subtle
   pill, the inactive one is bare text with a menu-hover tint. `aria-pressed` carries the
   state; the parent wraps the pair in `role="group"`. */
export const ViewStyleButton = (props: ButtonProps) => {
    const { localize } = useContext(AppContext);

    const icon = props.type === 'list'
        ? <ListViewStyleIcon size="small" ariaHidden={true} />
        : <GalleryViewStyleIcon size="small" ariaHidden={true} />;

    return (
        <ErrorCatcher componentName="ListStyleButton">
            <button
                type="button"
                onClick={props.onClick}
                aria-pressed={props.isActive}
                aria-label={props.type === 'list'
                    ? localize('switch-to-list-view', 'Switch to list view')
                    : localize('switch-to-gallery-view', 'Switch to gallery view')
                }
                class={clsx(
                    'inline-flex cursor-pointer items-center gap-cvi-3 rounded-cvi-md border-0 px-cvi-6 py-cvi-4',
                    'label-sm font-cvi-regular transition-colors duration-200 ease-cvi-standard',
                    props.isActive
                        ? 'bg-cvi-bg-neutral-subtle text-cvi-fg-default'
                        : 'bg-transparent text-cvi-fg-muted hover:bg-cvi-bg-menu-hover hover:text-cvi-fg-default',
                )}
            >
                {icon}
                <span>{props.text}</span>
            </button>
        </ErrorCatcher>
    );
};
